'use client';

import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

import YourHoldings from '@web/app/[locale]/(web)/trade/_misc/etc-section/spot/your-holdings';
import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

const SpotTabTypes = ['positions'] as const;
type SpotTabType = (typeof SpotTabTypes)[number];

const Tabs = createTabsComponent<SpotTabType>();

export const spotEtcTabTypeBunja = bunja(() => {
  const tabTypeAtom = atom<SpotTabType>('positions');

  return { tabTypeAtom };
});
spotEtcTabTypeBunja.debugLabel = 'spotEtcTabTypeBunja';

export const SpotEtcSection = () => {
  const t = useTranslations();
  const tabRef = useRef<HTMLDivElement>(null);

  const { symbol } = useBunja(tradeSymbolBunja);
  const { tabTypeAtom } = useBunja(spotEtcTabTypeBunja);
  const tabType = useAtomValue(tabTypeAtom);

  const tabLabelMapper: Record<string, string> = {
    positions: t('Assets.r250401.Your_holdings'),
  };

  const onChangeTab = async (tabValue: SpotTabType) => {
    segment.track(CommunityEvent.OrderFormInfoTabClick, {
      symbol,
      trade_type: 'spot',
      tab_type: tabLabelMapper[tabValue],
    });
  };

  const onClickTab = (tabValue: SpotTabType) => {
    if (tabType === tabValue) tabRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className={css({
        gridArea: 'etc',
        display: { base: 'none', tablet: 'flex' },
        flexDir: 'column',
        h: '100%',
        bgColor: 'background.default.level-0',
        rounded: 'var(--grid-rounded)',
        minW: 0,
        overflow: 'hidden',
      })}
      ref={tabRef}
    >
      <Tabs
        value={tabType}
        type='scrollable'
        border
        dragHandle
        onChange={onChangeTab}
        onClick={onClickTab}
        panelCss={{ px: 16, pb: 12 }}
      >
        <Tabs.Tab label={tabLabelMapper.positions} value='positions' />
      </Tabs>
      <div
        className={flex({
          px: 16,
          pb: 12,
          flex: 1,
          minH: 0,
          overflow: 'hidden',
          flexDir: 'column',
        })}
      >
        <YourHoldings />
      </div>
    </section>
  );
};
